import { inject, Injectable } from '@angular/core';
import { UtilitiesService } from "../../../services/utilities.service";
import { NavItemLabelIds, RoutePath } from "../../../enums/api.enum";
import { Router } from "@angular/router";
import { AutomationAction, AutomationType } from "../enums/automation.enum";
import { UiLayoutService } from "../../../ui-layout/services/ui-layout.service";

@Injectable()
export class AutomationUtilityService {
  #utilitiesService = inject(UtilitiesService);
  #uiLayoutService = inject(UiLayoutService);
  #router = inject(Router);

  /**
   * Navigates to the edit page for the specified automation item.
   * @param id - The ID of the item to edit.
   * @param type - The type of automation (WORKFLOW or INSTRUCTION).
   * @param action - The action to perform (should be EDIT).
   */
  editItem(id: string, type: AutomationType, action: AutomationAction): void {
    this.#navigateTo(type, action, id);
  }

  /**
   * Navigates to the clone page for the specified automation item.
   * @param id - The ID of the item to clone.
   * @param type - The type of automation (WORKFLOW or INSTRUCTION).
   * @param action - The action to perform (should be CLONE).
   */
  cloneItem(id: string, type: AutomationType, action: AutomationAction): void {
    this.#navigateTo(type, action, id);
  }

  /**
   * Navigates to the create page for the specified automation type.
   * @param type - The type of automation (WORKFLOW or INSTRUCTION).
   * @param action - The action to perform (should be CREATE).
   */
  createItem(type: AutomationType, action: AutomationAction): void {
    this.#navigateTo(type, action);
  }

  runItem(id: string, type: AutomationType, action: AutomationAction): void {
    this.#navigateTo(type, action, id);
  }

  /**
   * Navigates to the table/list view for the specified automation type and action.
   * @param type - The type of automation (WORKFLOW or INSTRUCTION).
   * @param action - The action to perform (MY_ITEMS or MANAGE_ITEMS).
   * @param locationFrom
   */
  routeToTable(type: AutomationType, action: AutomationAction, locationFrom?: string): void {
    this.#navigateTo(type, action, undefined, locationFrom);
  }

  /**
   * Handles navigation logic for automation actions and types and sets the locationFrom property.
   * @param type - The type of automation (WORKFLOW or INSTRUCTION).
   * @param action - The action to perform (CREATE, EDIT, CLONE, MY_ITEMS, MANAGE_ITEMS).
   * @param id - (Optional) The ID of the item for actions that require it (EDIT, CLONE).
   * @param locationFrom
   * @throws Error if the action is not supported.
   */
  #navigateTo(type: AutomationType, action: AutomationAction, id?: string, locationFrom?: string): void {
    this.#utilitiesService.locationFrom = locationFrom ?? this.#router.url;

    const isWorkflow = type === AutomationType.WORKFLOW;
    let selectedNavItem = NavItemLabelIds.Workflows; // Default to Workflows
    let path = '';

    switch (action) {
      case AutomationAction.CREATE:
        path = isWorkflow ? RoutePath.CreateWorkflow : RoutePath.CreateInstruction;
        selectedNavItem = isWorkflow ? NavItemLabelIds.CreateWorkflow : NavItemLabelIds.CreateInstruction;
        break;
      case AutomationAction.EDIT:
        path = isWorkflow ? `${RoutePath.EditWorkflow}/${id}` : `${RoutePath.EditInstruction}/${id}`;
        selectedNavItem = isWorkflow ? NavItemLabelIds.CreateWorkflow : NavItemLabelIds.CreateInstruction;
        break;
      case AutomationAction.CLONE:
        path = isWorkflow ? `${RoutePath.CloneWorkflow}/${id}` : `${RoutePath.CloneInstruction}/${id}`;
        selectedNavItem = isWorkflow ? NavItemLabelIds.CreateWorkflow : NavItemLabelIds.CreateInstruction;
        break;
      case AutomationAction.RUN:
        path = `${RoutePath.Workflows}/${id}`;
        selectedNavItem = NavItemLabelIds.Workflows;
        break;
      case AutomationAction.MY_ITEMS:
        path = isWorkflow ? RoutePath.MyWorkflows : RoutePath.MyInstructions;
        selectedNavItem = isWorkflow ? NavItemLabelIds.MyWorkflows : NavItemLabelIds.MyInstructions;
        break;
      case AutomationAction.MANAGE_ITEMS:
        path = isWorkflow ? RoutePath.ManageWorkflows : RoutePath.ManageInstructions;
        selectedNavItem = isWorkflow ? NavItemLabelIds.ManageWorkflows : NavItemLabelIds.ManageInstructions;
        break;
      default:
        throw new Error('Unsupported automation action');
    }

    this.#uiLayoutService.setSelectedNavItem(selectedNavItem);
    this.#router.navigateByUrl(path);
  }


}
